import { getLastKnownAuthUser } from "./authService";

export type PendingAdminImportKind = "material" | "allocation" | "ownership";

export interface PendingAdminImport {
  owner_user_id: number;
  kind: PendingAdminImportKind;
  filename: string;
  mimeType: string;
  size: number;
  savedAt: string;
  blob: Blob;
}

const DB_NAME = "materialentnahme-admin-imports";
const DB_VERSION = 2;
const STORE = "pending_v2";
const LEGACY_UNSCOPED_STORE = "pending";
const MAX_PENDING_IMPORT_BYTES = 128 * 1024 * 1024;

function currentOwnerId(): number | null { return getLastKnownAuthUser()?.id ?? null; }

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      // v1 war nur nach Datenart keyed. Auf gemeinsam genutzten Geräten konnte
      // ein anderer Admin dadurch eine lokal vorgemerkte Importdatei sehen. Die
      // alten unowned Datensätze bleiben deshalb unverändert im Legacy-Store als
      // Quarantäne erhalten, werden aber nie mehr von der Anwendung gelesen. So
      // gibt es weder Cross-User-Leak noch einen destruktiven Upgrade-Schritt.
      void LEGACY_UNSCOPED_STORE;
      if (!database.objectStoreNames.contains(STORE)) {
        const store = database.createObjectStore(STORE, { keyPath: ["owner_user_id", "kind"] });
        store.createIndex("owner_user_id", "owner_user_id", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Lokaler Importspeicher konnte nicht geöffnet werden."));
  });
}

export async function savePendingAdminImport(kind: PendingAdminImportKind, file: File): Promise<PendingAdminImport> {
  if (file.size > MAX_PENDING_IMPORT_BYTES) throw new Error("Importdatei ist für den lokalen Offline-Puffer zu groß.");
  if (navigator.storage?.estimate) {
    const estimate = await navigator.storage.estimate().catch(() => undefined);
    const hardLimit = Math.min(2 * 1024 * 1024 * 1024, estimate?.quota ? estimate.quota * 0.82 : Number.POSITIVE_INFINITY);
    if ((estimate?.usage ?? 0) + file.size >= hardLimit) throw new Error("Lokaler PWA-Speicher ist zu voll, um diese Importdatei sicher offline vorzumerken.");
  }
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) throw new Error("Importdatei kann ohne angemeldeten Benutzer nicht lokal vorgemerkt werden.");
  const value: PendingAdminImport = { owner_user_id: ownerUserId, kind, filename: file.name, mimeType: file.type, size: file.size, savedAt: new Date().toISOString(), blob: file };
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
  window.dispatchEvent(new Event("materialentnahme:status-refresh"));
  return value;
}

export async function getPendingAdminImport(kind: PendingAdminImportKind): Promise<PendingAdminImport | null> {
  if (!("indexedDB" in globalThis)) return null;
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) return null;
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).get([ownerUserId, kind]);
      request.onsuccess = () => resolve((request.result as PendingAdminImport | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

export async function getPendingAdminImports(): Promise<PendingAdminImport[]> {
  if (!("indexedDB" in globalThis)) return [];
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) return [];
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).index("owner_user_id").getAll(IDBKeyRange.only(ownerUserId));
      request.onsuccess = () => resolve((request.result as PendingAdminImport[]) ?? []);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

export async function deletePendingAdminImport(kind: PendingAdminImportKind): Promise<void> {
  if (!("indexedDB" in globalThis)) return;
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) return;
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete([ownerUserId, kind]);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
  window.dispatchEvent(new Event("materialentnahme:status-refresh"));
}

export function pendingAdminImportToFile(value: PendingAdminImport): File {
  return new File([value.blob], value.filename, { type: value.mimeType || "application/octet-stream", lastModified: new Date(value.savedAt).getTime() });
}
